import { useState } from "react";
import { Form, Button, Container, Row, Col, Card, Spinner, Table, Alert } from "react-bootstrap";
import { PersonFill, CreditCard2FrontFill, BuildingFill, ArrowLeft, FileEarmarkTextFill, CalendarFill } from "react-bootstrap-icons";

import axios from "axios";
import Swal from "sweetalert2";
import SignatureCanvas from "react-signature-canvas";
import { formatCurrency, calculateWithdrawalSettlement, baseUrl } from "../../store/config";
import pioneers from "../../data/pioneers";

function BeneficiaryWithdrawalForm() {
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [step, setStep] = useState(1);
    const [dateOfDeath, setDateOfDeath] = useState(new Date());
    const [memberData, setMemberData] = useState(null);
    const [isPioneer, setIsPioneer] = useState(false);
    const [balance, setBalance] = useState(null);
    const [calculatedWithdrawal, setCalculatedWithdrawal] = useState(null);
    const [isCalculating, setIsCalculating] = useState(false);
    const [signatureRef, setSignatureRef] = useState(null);
    const [passportFile, setPassportFile] = useState(null);
    const [signatureImage, setSignatureImage] = useState(null);
    const [documentFiles, setDocumentFiles] = useState({
        deathCertificate: null,
        identification: null,
        proofOfRelationship: null,
        contributionStatement: null,
        letterOfAdministration: null
    });

    // Form data state
    const [formData, setFormData] = useState({
        // Deceased Member Details
        deceasedFullName: "",
        memberId: "",
        lastKnownAddress: "",

        // Beneficiary Details
        beneficiaryFullName: "",
        relationship: "",
        phoneNumber: "",
        emailAddress: "",
        address: "",

        // Bank Details
        bankName: "",
        accountName: "",
        accountNumber: "",
    });

    // Handle member ID search
    const searchMember = async () => {
        if (!formData.memberId) {
            Swal.fire({
                icon: "error",
                title: "Member ID Required",
                text: "Please enter a valid Member ID to proceed."
            });
            return;
        }

        setIsLoading(true);
        try {
            const encodedId = encodeURIComponent(formData.memberId);
            const response = await axios.get(

                `${baseUrl}/user/getmemberbymemberid/${encodedId}`,
                {
                    headers: {
                        "Content-Type": "application/json",

                    },
                    timeout: 10000,
                }
            );

            if (response.data) {
                setMemberData(response.data);
                setFormData({
                    ...formData,
                    deceasedFullName: response.data.name || "",
                    lastKnownAddress: response.data.homeAddress || ""
                });
                setIsPioneer(pioneers.includes(response.data.memberId || ""));
                await fetchMemberBalance(formData.memberId);
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Member Not Found",
                    text: "No member found with the provided ID."
                });
            }
        } catch (error) {
            console.error("API Error:", error);
            Swal.fire({
                icon: "error",
                title: "Search Failed",
                text: error.response?.data?.message || "Unable to fetch member details."
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch member balance
    const fetchMemberBalance = async (memberId) => {
        try {

            const response = await axios.get(
                `${baseUrl}/user/get-balances-public`,
                {
                    params: { id: memberId },
                    timeout: 5000,
                }
            );
            if (response.data) {
                setBalance(response.data);
            }
        } catch (error) {
            console.error("Error fetching balance:", error);
            Swal.fire({
                icon: "warning",
                title: "Balance Data Unavailable",
                text: "Could not retrieve balance information for this member."
            });
        }
    };

    // Calculate withdrawal amount
    const calculateWithdrawal = () => {
        setIsCalculating(true);

        // Check if balance data is available
        if (!balance?.summary) {
            Swal.fire({
                icon: "error",
                title: "Balance Not Available",
                text: "Unable to fetch account balance. Please try again."
            });
            setIsCalculating(false);
            return;
        }

        try {
            const settlement = calculateWithdrawalSettlement(balance, isPioneer, { applyServiceCharge: false });
            setCalculatedWithdrawal(settlement);

            // Check if amount due is positive
            if (settlement.amountDue < 0) {
                Swal.fire({
                    icon: "warning",
                    title: "Outstanding Loans",
                    text: "The deceased member has outstanding loans that exceed the available settlement entitlements. Please contact the cooperative office for further assistance."
                });
            }
        } catch (error) {
            console.error("Error calculating withdrawal:", error);
            Swal.fire({
                icon: "error",
                title: "Calculation Error",
                text: "An error occurred while calculating withdrawal amount. Please try again."
            });
        } finally {
            setIsCalculating(false);
        }
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    // Handle file uploads
    const handleFileChange = (e) => {
        const { name, files } = e.target;

        if (name === "passport") {
            setPassportFile(files[0]);
        } else {
            setDocumentFiles({
                ...documentFiles,
                [name]: files[0]
            });
        }
    };

    // Handle signature pad
    const handleSignaturePad = (signaturePad) => {
        setSignatureRef(signaturePad);
    };

    // Clear signature
    const clearSignature = () => {
        if (signatureRef) {
            signatureRef.clear();
            setSignatureImage(null);
        }
    };

    // Save signature
    const saveSignature = () => {
        if (signatureRef && !signatureRef.isEmpty()) {
            const signatureDataURL = signatureRef.toDataURL();
            setSignatureImage(signatureDataURL);
            Swal.fire({
                icon: "success",
                title: "Signature Saved",
                showConfirmButton: false,
                timer: 1500
            });
        } else {
            Swal.fire({
                icon: "warning",
                title: "Empty Signature",
                text: "Please sign before saving."
            });
        }
    };

    // Helper to convert base64 dataURL to Blob
    function dataURLToBlob(dataURL) {
        const arr = dataURL.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);

        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }

        return new Blob([u8arr], { type: mime });
    }

    const resetForm = () => {
        setFormData(prev => ({
            ...Object.fromEntries(Object.keys(prev).map(key => [key, ""]))
        }));

        setDocumentFiles({
            deathCertificate: null,
            identification: null,
            proofOfRelationship: null,
            contributionStatement: null,
            letterOfAdministration: null
        });

        setPassportFile(null);
        setSignatureImage(null);
        setMemberData(null);
        setIsPioneer(false);
        setBalance(null);
        setCalculatedWithdrawal(null);
        setStep(1);
        setDateOfDeath(new Date());

        if (signatureRef?.clear) {
            signatureRef.clear();
        }
    };

    // Submit form
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Step 1: Validate Member ID
        if (step === 1 && !formData.memberId) {
            Swal.fire({
                icon: "error",
                title: "Member ID Required",
                text: "Please enter a valid Member ID to proceed."
            });
            return;
        }

        // Step 2: Ensure withdrawal is calculated
        if (step === 2) {
            if (!calculatedWithdrawal) {
                Swal.fire({
                    icon: "warning",
                    title: "Calculation Required",
                    text: "Please calculate withdrawal details before proceeding."
                });
                return;
            }
            setStep(3);
            return;
        }

        // Step 3: Validate beneficiary details
        if (step === 3) {
            const requiredFields = [
                "beneficiaryFullName", "relationship", "phoneNumber",
                "emailAddress", "address"
            ];
            const missingFields = requiredFields.filter(field => !formData[field]);

            if (missingFields.length > 0) {
                Swal.fire({
                    icon: "error",
                    title: "Missing Information",
                    text: "Please fill in all required beneficiary details."
                });
                return;
            }
            setStep(4);
            return;
        }

        // Step 4: Validate bank details
        if (step === 4) {
            const requiredBankFields = ["bankName", "accountName", "accountNumber"];
            const missingBankFields = requiredBankFields.filter(field => !formData[field]);

            if (missingBankFields.length > 0) {
                Swal.fire({
                    icon: "error",
                    title: "Missing Bank Details",
                    text: "Please fill in all required bank information."
                });
                return;
            }
            setStep(5);
            return;
        }

        // Step 5: Validate documents and submit
        if (step === 5) {
            const requiredDocs = ["deathCertificate", "identification", "proofOfRelationship"];
            const missingDocs = requiredDocs.filter(doc => !documentFiles[doc]);

            if (missingDocs.length > 0) {
                Swal.fire({
                    icon: "error",
                    title: "Missing Documents",
                    text: "Please upload all required documents."
                });
                return;
            }

            if (!passportFile) {
                Swal.fire({
                    icon: "error",
                    title: "Passport Photo Required",
                    text: "Please upload your passport photograph."
                });
                return;
            }

            if (!signatureImage) {
                Swal.fire({
                    icon: "error",
                    title: "Signature Required",
                    text: "Please sign the form before submitting."
                });
                return;
            }

            setIsSubmitting(true);

            try {
                const submissionFormData = new FormData();

                // Rename and map fields to backend's expectation
                submissionFormData.append("deceasedMemberId", formData.memberId);
                submissionFormData.append("beneficiaryName", formData.beneficiaryFullName);
                submissionFormData.append("beneficiaryEmail", formData.emailAddress);
                submissionFormData.append("beneficiaryPhone", formData.phoneNumber);
                submissionFormData.append("relationship", formData.relationship);
                submissionFormData.append("beneficiaryAddress", formData.address);

                // Add structured bank details
                submissionFormData.append("bankDetails", JSON.stringify({
                    bankName: formData.bankName,
                    accountName: formData.accountName,
                    accountNumber: formData.accountNumber
                }));

                // Add calculation and death info
                submissionFormData.append("dateOfDeath", dateOfDeath.toISOString());
                submissionFormData.append("withdrawalCalculation", JSON.stringify(calculatedWithdrawal));

                // Add document files
                submissionFormData.append("passport", passportFile);
                Object.keys(documentFiles).forEach(key => {
                    if (documentFiles[key]) {
                        submissionFormData.append(key, documentFiles[key]);
                    }
                });

                // Add signature
                if (signatureImage.startsWith("data:image")) {
                    const signatureBlob = dataURLToBlob(signatureImage);
                    submissionFormData.append("signatureImage", signatureBlob, "signature.png");
                } else {
                    submissionFormData.append("signatureImage", signatureImage);
                }
                submissionFormData.append("submissionDate", new Date().toISOString());

                // Submit to backend
                await axios.post(`${baseUrl}/api/beneficiary-withdrawal`, submissionFormData);

                Swal.fire({
                    icon: "success",
                    title: "Form Submitted Successfully",
                    text: "Your beneficiary withdrawal request has been submitted. The cooperative will review your application and contact you shortly."
                });

                setTimeout(() => {
                    resetForm();
                }, 2000);

            } catch (error) {
                console.error("Error submitting form:", error);
                Swal.fire({
                    icon: "error",
                    title: "Submission Failed",
                    text: error.response?.data?.message || "Failed to submit withdrawal request. Please try again later."
                });
            } finally {
                setIsSubmitting(false);
            }
        }
    };


    // Go to previous step
    const goToPreviousStep = () => {
        setStep(Math.max(1, step - 1));
    };

    return (
        <Container className="mt-4 pb-5">
            <Card className="shadow-sm">
                <Card.Header className="bg-success text-white text-center py-3">
                    <h4 className="mb-0">LION MULTI-PURPOSE COOPERATIVE SOCIETY LTD</h4>
                    <h5 className="mb-0 mt-2">BENEFICIARY WITHDRAWAL FORM</h5>
                </Card.Header>
                <Card.Body className="px-3 px-md-4 py-4">
                    {/* Progress Indicator */}
                    <div className="mb-4">
                        <div className="d-flex justify-content-between mb-1">
                            {["Member Details", "Withdrawal Calculation", "Beneficiary Details", "Bank Details", "Documents & Declaration"].map((label, index) => (
                                <div key={index} className={`text-center ${index + 1 === step ? "fw-bold text-success" : "text-muted"}`} style={{ fontSize: "0.8rem" }}>
                                    {label}
                                </div>
                            ))}
                        </div>
                        <div className="progress" style={{ height: "10px" }}>
                            <div
                                className="progress-bar bg-success"
                                role="progressbar"
                                style={{ width: `${step * 20}%` }}
                                aria-valuenow={step * 20}
                                aria-valuemin="0"
                                aria-valuemax="100"
                            ></div>
                        </div>
                    </div>

                    <Form onSubmit={handleSubmit}>
                        {/* Step 1: Deceased Member Details */}
                        {step === 1 && (
                            <>
                                <Card className="mb-4">
                                    <Card.Header className="bg-light">
                                        <h5 className="mb-0">SECTION A: DECEASED MEMBER DETAILS</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <Row className="g-3">
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Membership Number <span className="text-danger">*</span></Form.Label>
                                                    <div className="input-group">
                                                        <Form.Control
                                                            type="text"
                                                            name="memberId"
                                                            value={formData.memberId}
                                                            onChange={handleInputChange}
                                                            placeholder="Enter Member ID"
                                                            required
                                                        />
                                                        <Button
                                                            variant="outline-success"
                                                            onClick={searchMember}
                                                            disabled={isLoading}
                                                        >
                                                            {isLoading ? (
                                                                <Spinner animation="border" size="sm" />
                                                            ) : "Search"}
                                                        </Button>
                                                    </div>
                                                    <Form.Text className="text-muted">
                                                        Enter the Membership ID of the deceased member
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Date of Death <span className="text-danger">*</span></Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        value={dateOfDeath.toISOString().split('T')[0]}
                                                        onChange={(e) => setDateOfDeath(new Date(e.target.value))}
                                                        max={new Date().toISOString().split('T')[0]}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={12}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Full Name of Deceased</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="deceasedFullName"
                                                        value={formData.deceasedFullName}
                                                        onChange={handleInputChange}
                                                        placeholder="Full name will appear after searching with Member ID"
                                                        readOnly
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={12}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Last Known Address</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="lastKnownAddress"
                                                        value={formData.lastKnownAddress}
                                                        onChange={handleInputChange}
                                                        placeholder="Address will appear after searching with Member ID"
                                                        readOnly
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        {memberData && (
                                            <Alert variant="success" className="mt-3">
                                                <Alert.Heading>Member Found</Alert.Heading>
                                                <p>Member details have been retrieved successfully. Please click "Next" to proceed.</p>
                                            </Alert>
                                        )}
                                    </Card.Body>
                                </Card>

                                <div className="d-flex justify-content-between mt-4">
                                    <div></div>
                                    <Button
                                        variant="success"
                                        type="button"
                                        onClick={() => setStep(2)}
                                        disabled={!memberData}
                                    >
                                        Next <ArrowLeft className="rotate-180" />
                                    </Button>
                                </div>
                            </>
                        )}

                        {/* Step 2: Withdrawal Calculation */}
                        {step === 2 && (
                            <>
                                <Card className="mb-4">
                                    <Card.Header className="bg-light">
                                        <h5 className="mb-0">SECTION A: SAVINGS AND LOANS BALANCE</h5>
                                        <p className="text-muted mb-0 small">As of {new Date().toLocaleDateString()}</p>
                                    </Card.Header>
                                    <Card.Body>
                                        <div className="d-flex justify-content-end mb-4">
                                            <Button
                                                variant="success"
                                                onClick={calculateWithdrawal}
                                                disabled={isCalculating}
                                            >
                                                {isCalculating ? (
                                                    <>
                                                        <Spinner animation="border" size="sm" className="me-2" />
                                                        Calculating...
                                                    </>
                                                ) : "Calculate Withdrawal Amount"}
                                            </Button>
                                        </div>

                                        {calculatedWithdrawal && (
                                            <div>
                                                {calculatedWithdrawal.savingsItems.length > 0 && (
                                                    <>
                                                        <h6>Savings Balance:</h6>
                                                        <Table bordered hover responsive className="mb-4">
                                                            <thead className="table-success">
                                                                <tr>
                                                                    <th>Savings Type</th>
                                                                    <th className="text-end">Amount</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {calculatedWithdrawal.savingsItems.map((item, index) => (
                                                                    <tr key={index}>
                                                                        <td>{item.name}</td>
                                                                        <td className="text-end">{formatCurrency(item.value)}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                            <tfoot className="table-light">
                                                                <tr>
                                                                    <th>Total Savings</th>
                                                                    <th className="text-end">{formatCurrency(calculatedWithdrawal.totalSavings)}</th>
                                                                </tr>
                                                            </tfoot>
                                                        </Table>
                                                    </>
                                                )}

                                                {calculatedWithdrawal.claimItems.length > 0 && (
                                                    <>
                                                        <h6>Claim Entitlements:</h6>
                                                        <Table bordered hover responsive className="mb-4">
                                                            <thead className="table-info">
                                                                <tr>
                                                                    <th>Claim Type</th>
                                                                    <th className="text-end">Amount</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {calculatedWithdrawal.claimItems.map((item, index) => (
                                                                    <tr key={index}>
                                                                        <td>{item.name}</td>
                                                                        <td className="text-end">{formatCurrency(item.value)}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                            <tfoot className="table-light">
                                                                <tr>
                                                                    <th>Total Claims</th>
                                                                    <th className="text-end">{formatCurrency(calculatedWithdrawal.totalClaims)}</th>
                                                                </tr>
                                                            </tfoot>
                                                        </Table>
                                                    </>
                                                )}

                                                {calculatedWithdrawal.loanItems.length > 0 && (
                                                    <>
                                                        <h6>Outstanding Loans:</h6>
                                                        <Table bordered hover responsive className="mb-4">
                                                            <thead className="table-danger">
                                                                <tr>
                                                                    <th>Loan Type</th>
                                                                    <th className="text-end">Outstanding Amount</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {calculatedWithdrawal.loanItems.map((item, index) => (
                                                                    <tr key={index}>
                                                                        <td>{item.name}</td>
                                                                        <td className="text-end">{formatCurrency(item.value)}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                            <tfoot className="table-light">
                                                                <tr>
                                                                    <th>Total Loans</th>
                                                                    <th className="text-end">{formatCurrency(calculatedWithdrawal.totalLoans)}</th>
                                                                </tr>
                                                            </tfoot>
                                                        </Table>
                                                    </>
                                                )}

                                                <Card className="bg-light mb-4">
                                                    <Card.Body>
                                                        <Table borderless className="mb-0">
                                                            <tbody>
                                                                <tr>
                                                                    <th>Total Savings Balance</th>
                                                                    <td className="text-end">{formatCurrency(calculatedWithdrawal.totalSavings)}</td>
                                                                </tr>
                                                                {calculatedWithdrawal.totalClaims > 0 && (
                                                                    <tr>
                                                                        <th>Life Assurance Claim</th>
                                                                        <td className="text-end">{formatCurrency(calculatedWithdrawal.totalClaims)}</td>
                                                                    </tr>
                                                                )}
                                                                <tr>
                                                                    <th>Outstanding Loans</th>
                                                                    <td className="text-end">({formatCurrency(calculatedWithdrawal.totalLoans)})</td>
                                                                </tr>
                                                                <tr className="table-success">
                                                                    <th className="h5">Net Amount Due</th>
                                                                    <td className="text-end h5">{formatCurrency(calculatedWithdrawal.amountDue)}</td>
                                                                </tr>
                                                            </tbody>
                                                        </Table>
                                                    </Card.Body>
                                                </Card>

                                                <Alert variant="info">
                                                    <p className="mb-0"><strong>Note:</strong> No service charge is applied for beneficiary withdrawals. Life Assurance Claim is shown separately from savings.</p>
                                                </Alert>
                                            </div>
                                        )}

                                        {!calculatedWithdrawal && (
                                            <Alert variant="secondary">
                                                <p className="mb-0 text-center">Click "Calculate Withdrawal Amount" to view the deceased member's balance details.</p>
                                            </Alert>
                                        )}
                                    </Card.Body>
                                </Card>

                                <div className="d-flex justify-content-between mt-4">
                                    <Button
                                        variant="outline-secondary"
                                        type="button"
                                        onClick={goToPreviousStep}
                                    >
                                        <ArrowLeft /> Back
                                    </Button>
                                    <Button
                                        variant="success"
                                        type="submit"
                                    >
                                        Next <ArrowLeft className="rotate-180" />
                                    </Button>
                                </div>
                            </>
                        )}

                        {/* Step 3: Beneficiary Details */}
                        {step === 3 && (
                            <>
                                <Card className="mb-4">
                                    <Card.Header className="bg-light">
                                        <h5 className="mb-0">SECTION B: BENEFICIARY DETAILS</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <Row className="g-3">
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Full Name of Beneficiary <span className="text-danger">*</span></Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="beneficiaryFullName"
                                                        value={formData.beneficiaryFullName}
                                                        onChange={handleInputChange}
                                                        placeholder="Enter your full name"
                                                        required
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Relationship to Deceased <span className="text-danger">*</span></Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="relationship"
                                                        value={formData.relationship}
                                                        onChange={handleInputChange}
                                                        placeholder="e.g., Spouse, Child, Parent"
                                                        required
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Phone Number <span className="text-danger">*</span></Form.Label>
                                                    <Form.Control
                                                        type="tel"
                                                        name="phoneNumber"
                                                        value={formData.phoneNumber}
                                                        onChange={handleInputChange}
                                                        placeholder="Enter your phone number"
                                                        required
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Email Address <span className="text-danger">*</span></Form.Label>
                                                    <Form.Control
                                                        type="email"
                                                        name="emailAddress"
                                                        value={formData.emailAddress}
                                                        onChange={handleInputChange}
                                                        placeholder="Enter your email address"
                                                        required
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={12}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Address <span className="text-danger">*</span></Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={3}
                                                        name="address"
                                                        value={formData.address}
                                                        onChange={handleInputChange}
                                                        placeholder="Enter your full address"
                                                        required
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>

                                <div className="d-flex justify-content-between mt-4">
                                    <Button
                                        variant="outline-secondary"
                                        type="button"
                                        onClick={goToPreviousStep}
                                    >
                                        <ArrowLeft /> Back
                                    </Button>
                                    <Button
                                        variant="success"
                                        type="submit"
                                    >
                                        Next <ArrowLeft className="rotate-180" />
                                    </Button>
                                </div>
                            </>
                        )}

                        {/* Step 4: Bank Details */}
                        {step === 4 && (
                            <>
                                <Card className="mb-4">
                                    <Card.Header className="bg-light">
                                        <h5 className="mb-0">SECTION C: BANK DETAILS FOR PAYMENT</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <Row className="g-3">
                                            <Col md={12}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label><BuildingFill className="me-2" />Bank Name <span className="text-danger">*</span></Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="bankName"
                                                        value={formData.bankName}
                                                        onChange={handleInputChange}
                                                        placeholder="Enter your bank name"
                                                        required
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label><PersonFill className="me-2" />Account Name <span className="text-danger">*</span></Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="accountName"
                                                        value={formData.accountName}
                                                        onChange={handleInputChange}
                                                        placeholder="Enter the account name"
                                                        required
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label><CreditCard2FrontFill className="me-2" />Account Number <span className="text-danger">*</span></Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="accountNumber"
                                                        value={formData.accountNumber}
                                                        onChange={handleInputChange}
                                                        placeholder="Enter your account number"
                                                        required
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>

                                <div className="d-flex justify-content-between mt-4">
                                    <Button
                                        variant="outline-secondary"
                                        type="button"
                                        onClick={goToPreviousStep}
                                    >
                                        <ArrowLeft /> Back
                                    </Button>
                                    <Button
                                        variant="success"
                                        type="submit"
                                    >
                                        Next <ArrowLeft className="rotate-180" />
                                    </Button>
                                </div>
                            </>
                        )}

                        {/* Step 5: Documents & Declaration */}
                        {step === 5 && (
                            <>
                                <Card className="mb-4">
                                    <Card.Header className="bg-light">
                                        <h5 className="mb-0">SECTION D: REQUIRED DOCUMENTS</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <Row className="g-3">
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>
                                                        <FileEarmarkTextFill className="me-2" />
                                                        Death Certificate <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="file"
                                                        name="deathCertificate"
                                                        onChange={handleFileChange}
                                                        required
                                                    />
                                                    <Form.Text className="text-muted">
                                                        Upload a scanned copy of the death certificate
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>
                                                        <FileEarmarkTextFill className="me-2" />
                                                        Valid Identification <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="file"
                                                        name="identification"
                                                        onChange={handleFileChange}
                                                        required
                                                    />
                                                    <Form.Text className="text-muted">
                                                        National ID, Voter's Card, Driver's License
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>
                                                        <FileEarmarkTextFill className="me-2" />
                                                        Proof of Relationship <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="file"
                                                        name="proofOfRelationship"
                                                        onChange={handleFileChange}
                                                        required
                                                    />
                                                    <Form.Text className="text-muted">
                                                        Marriage Certificate, Birth Certificate, Affidavit
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>
                                                        <FileEarmarkTextFill className="me-2" />
                                                        Last Contribution Statement
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="file"
                                                        name="contributionStatement"
                                                        onChange={handleFileChange}
                                                    />
                                                    <Form.Text className="text-muted">
                                                        If available
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>
                                                        <FileEarmarkTextFill className="me-2" />
                                                        Letter of Administration
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="file"
                                                        name="letterOfAdministration"
                                                        onChange={handleFileChange}
                                                    />
                                                    <Form.Text className="text-muted">
                                                        If available
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>
                                                        <FileEarmarkTextFill className="me-2" />
                                                        Passport Photograph <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="file"
                                                        name="passport"
                                                        onChange={handleFileChange}
                                                        required
                                                    />
                                                    <Form.Text className="text-muted">
                                                        Recent passport photograph of beneficiary
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>

                                <Card className="mb-4">
                                    <Card.Header className="bg-light">
                                        <h5 className="mb-0">SECTION E: DECLARATION</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <Alert variant="light" className="border">
                                            <p>
                                                I, <strong>{formData.beneficiaryFullName || "[Your Full Name]"}</strong>, hereby declare that I am the rightful
                                                beneficiary of the deceased member, <strong>{formData.deceasedFullName || "[Deceased Member's Full Name]"}</strong>,
                                                and am entitled to receive the savings/entitlements as per the cooperative society's rules and regulations.
                                                I understand that any outstanding loans or liabilities will be deducted from the deceased member's
                                                benefits as per the society's policies.
                                            </p>
                                        </Alert>

                                        <Row className="g-3 mt-2">
                                            <Col md={12}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label><CalendarFill className="me-2" />Date</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={new Date().toLocaleDateString()}
                                                        readOnly
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={12}>
                                                <Form.Group className="mb-4">
                                                    <Form.Label>Signature <span className="text-danger">*</span></Form.Label>
                                                    <div className="border rounded p-3 mb-2" style={{ backgroundColor: "#f8f9fa" }}>
                                                        <SignatureCanvas
                                                            ref={handleSignaturePad}
                                                            penColor="black"
                                                            canvasProps={{
                                                                width: '100%',
                                                                height: 150,
                                                                className: 'signature-canvas'
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="d-flex gap-2">
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            onClick={clearSignature}
                                                        >
                                                            Clear
                                                        </Button>
                                                        <Button
                                                            variant="outline-success"
                                                            size="sm"
                                                            onClick={saveSignature}
                                                        >
                                                            Save Signature
                                                        </Button>
                                                    </div>
                                                    {signatureImage && (
                                                        <Alert variant="success" className="mt-2">
                                                            <p className="mb-0 small">Signature saved successfully</p>
                                                        </Alert>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>

                                <div className="d-flex justify-content-between mt-4">
                                    <Button
                                        variant="outline-secondary"
                                        type="button"
                                        onClick={goToPreviousStep}
                                    >
                                        <ArrowLeft /> Back
                                    </Button>
                                    <Button
                                        variant="success"
                                        type="submit"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Submitting...
                                            </>
                                        ) : "Submit Application"}
                                    </Button>
                                </div>
                            </>
                        )}
                    </Form>
                </Card.Body>
            </Card>
        </Container>
    );
}

export default BeneficiaryWithdrawalForm;
